type ThemeName = "day" | "night";

type ThemeOptions = {
  storage?: Storage;
  switcher?: HTMLButtonElement;
};

const STORAGE_KEY = "preferred-theme";

export default class Theme {
  time: Date;
  theme: ThemeName;
  body: HTMLElement;
  storage?: Storage;
  switcher?: HTMLButtonElement;

  constructor(time: Date, body: HTMLElement, options: ThemeOptions = {}) {
    this.time = time;
    this.body = body;
    this.storage = options.storage ?? this.defaultStorage();
    this.switcher = options.switcher;
    this.theme = this.defaultTheme();
    this.setTheme();
  }

  defaultTheme(): ThemeName {
    const savedTheme = this.savedTheme();
    if (savedTheme) return savedTheme;

    if (this.prefersDarkTheme()) return "night";

    const hour = this.time.getHours();
    if (hour > 6 && hour < 20) return "day";
    return "night";
  }

  toggleTheme(): ThemeName {
    this.theme = this.theme === "day" ? "night" : "day";
    this.saveTheme();

    this.setTheme();
    return this.theme;
  }

  setTheme(): void {
    this.body.classList.toggle("day", this.theme === "day");
    this.body.classList.toggle("night", this.theme === "night");

    if (this.switcher) {
      this.switcher.setAttribute("aria-pressed", String(this.theme === "night"));
      this.switcher.textContent =
        this.theme === "day" ? "switch to night theme" : "switch to day theme";
    }
  }

  private defaultStorage(): Storage | undefined {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  }

  private savedTheme(): ThemeName | undefined {
    try {
      const savedTheme = this.storage?.getItem(STORAGE_KEY);
      return savedTheme === "day" || savedTheme === "night" ? savedTheme : undefined;
    } catch {
      return undefined;
    }
  }

  private saveTheme(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, this.theme);
    } catch {
      // Theme changes should still work when storage is unavailable.
    }
  }

  private prefersDarkTheme(): boolean {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }
}
